import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useActiveMatch } from '../contexts/ActiveMatchContext';
import { usePlayerPopup } from '../contexts/PlayerPopupContext';
import { getRankInfo, RANK_COLORS, getTierBounds } from '../rank';

// Rank path data — XP values match the new 60-level system (750 XP/level, 500 for Bronze)
const RANK_DISPLAY = [
  { group: 'Bronze',      color: '#cd7f32', tiers: [
    { label: 'Bronze I',      icon: '/ranks/Bronze_1_Rank.png',      xpToNext: 500,  levels: 'Lv 1'     },
    { label: 'Bronze II',     icon: '/ranks/Bronze_2_Rank.png',      xpToNext: 500,  levels: 'Lv 2'     },
    { label: 'Bronze III',    icon: '/ranks/Bronze_3_Rank.png',      xpToNext: 500,  levels: 'Lv 3'     },
  ]},
  { group: 'Silver',      color: '#a8b2bd', tiers: [
    { label: 'Silver I',      icon: '/ranks/Silver_1_Rank.png',      xpToNext: 750,  levels: 'Lv 4'     },
    { label: 'Silver II',     icon: '/ranks/Silver_2_Rank.png',      xpToNext: 750,  levels: 'Lv 5'     },
    { label: 'Silver III',    icon: '/ranks/Silver_3_Rank.png',      xpToNext: 750,  levels: 'Lv 6'     },
  ]},
  { group: 'Gold',        color: '#c9940a', tiers: [
    { label: 'Gold I',        icon: '/ranks/Gold_1_Rank.png',        xpToNext: 1500, levels: 'Lv 7–8'   },
    { label: 'Gold II',       icon: '/ranks/Gold_2_Rank.png',        xpToNext: 1500, levels: 'Lv 9–10'  },
    { label: 'Gold III',      icon: '/ranks/Gold_3_Rank.png',        xpToNext: 1500, levels: 'Lv 11–12' },
  ]},
  { group: 'Platinum',    color: '#4fc4cf', tiers: [
    { label: 'Platinum I',    icon: '/ranks/Platinum_1_Rank.png',    xpToNext: 1500, levels: 'Lv 13–14' },
    { label: 'Platinum II',   icon: '/ranks/Platinum_2_Rank.png',    xpToNext: 1500, levels: 'Lv 15–16' },
    { label: 'Platinum III',  icon: '/ranks/Platinum_3_Rank.png',    xpToNext: 1500, levels: 'Lv 17–18' },
  ]},
  { group: 'Diamond',     color: '#4a90e2', tiers: [
    { label: 'Diamond I',     icon: '/ranks/Diamond_1_Rank.png',     xpToNext: 2250, levels: 'Lv 19–21' },
    { label: 'Diamond II',    icon: '/ranks/Diamond_2_Rank.png',     xpToNext: 2250, levels: 'Lv 22–24' },
    { label: 'Diamond III',   icon: '/ranks/Diamond_3_Rank.png',     xpToNext: 2250, levels: 'Lv 25–27' },
  ]},
  { group: 'Ascendant',   color: '#3dd68c', tiers: [
    { label: 'Ascendant I',   icon: '/ranks/Ascendant_1_Rank.png',   xpToNext: 2250, levels: 'Lv 28–30' },
    { label: 'Ascendant II',  icon: '/ranks/Ascendant_2_Rank.png',   xpToNext: 2250, levels: 'Lv 31–33' },
    { label: 'Ascendant III', icon: '/ranks/Ascendant_3_Rank.png',   xpToNext: 3000, levels: 'Lv 34–37' },
  ]},
  { group: 'Master',      color: '#8b1a2f', tiers: [
    { label: 'Master I',      icon: '/ranks/Master_1_Rank.png',      xpToNext: 3000, levels: 'Lv 38–41' },
    { label: 'Master II',     icon: '/ranks/Master_2_Rank.png',      xpToNext: 3000, levels: 'Lv 42–45' },
    { label: 'Master III',    icon: '/ranks/Master_3_Rank.png',      xpToNext: 3000, levels: 'Lv 46–49' },
  ]},
  { group: 'Grandmaster', color: '#ff4655', tiers: [
    { label: 'Grandmaster',   icon: '/ranks/Grandmaster_Rank.png',   xpToNext: null, levels: 'Lv 50+'   },
  ]},
];

const API = process.env.REACT_APP_API_URL || '';

// Module-level caches — survive unmount/remount so the sidebar never flashes empty
let _recentGamesCache = [];
let _activeCountCache = 0;
let _onlineCountCache = 0;

export default function Home() {
  const { user, refreshUser } = useAuth();
  const { activeMatch, secsUntilForfeit } = useActiveMatch();
  const { openPopup } = usePlayerPopup();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [activeCount, setActiveCount] = useState(_activeCountCache);
  const [onlineCount, setOnlineCount] = useState(_onlineCountCache);
  const [recentGames, setRecentGames] = useState(_recentGamesCache);
  const [dailyWins, setDailyWins] = useState(0);
  const [showRankInfo, setShowRankInfo] = useState(false);

  useEffect(() => {
    refreshUser();

    axios.get(`${API}/api/match-history`)
      .then(r => setHistory(r.data))
      .catch(() => {});

    if (user) {
      axios.get(`${API}/api/my-rank`)
        .then(r => setDailyWins(r.data.dailyWins ?? 0))
        .catch(() => {});
    }

    const fetchLive = () => {
      axios.get(`${API}/api/active-matches`).then(r => { _activeCountCache = r.data.count; setActiveCount(r.data.count); }).catch(() => {});
      axios.get(`${API}/api/recent-games`).then(r => { _recentGamesCache = r.data; setRecentGames(r.data); }).catch(() => {});
      axios.get(`${API}/api/online-count`).then(r => { _onlineCountCache = r.data.count; setOnlineCount(r.data.count); }).catch(() => {});
    };
    fetchLive();
    const liveInterval = setInterval(fetchLive, 10000);
    return () => clearInterval(liveInterval);
  }, []);

  const winRate = user ? (user.wins + user.losses > 0
    ? Math.round((user.wins / (user.wins + user.losses)) * 100) : 0) : 0;

  return (
    <div className="home-layout">

      {/* ── Main content ── */}
      <div className="home-main">
        <div className="home-hero">
          <h1>
            Welcome back,{' '}
            {user && (() => {
              const r = getRankInfo(user.rating ?? 0);
              return (
                <span style={{ whiteSpace: 'nowrap' }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: 'rgba(255,255,255,0.12)', borderRadius: 8,
                    padding: '2px 8px 2px 6px', fontSize: '0.82em',
                    fontWeight: 800, verticalAlign: 'middle',
                    border: `1.5px solid ${r.color}44`,
                    color: r.color, marginRight: 6,
                  }}>
                    {r.level}
                    <img src={r.icon} alt={r.label} style={{ width: 18, height: 18, objectFit: 'contain', display: 'inline-block' }} />
                  </span>
                  {(user?.display_name || user?.name)?.split(' ')[0]}
                </span>
              );
            })()}
            {!user && (user?.display_name || user?.name)?.split(' ')[0]}
          </h1>
          <p>Challenge other students in live SAT practice matches and climb the global rankings.</p>
          <div className="online-count">
            {onlineCount} player{onlineCount !== 1 ? 's' : ''} SATing<span className="ellipsis-anim" />
          </div>
          <div className="home-actions">
            {activeMatch?.gameId ? (
              <div className="home-rejoin-wrap">
                <button
                  className="btn btn-rejoin btn-lg"
                  onClick={() => navigate(`/game/${activeMatch.gameId}`)}
                >
                  ↩ Rejoin Match
                </button>
                {secsUntilForfeit !== null && secsUntilForfeit > 0 && (
                  <div className="home-forfeit-countdown">
                    ⚠ Auto-resign in {secsUntilForfeit}s
                  </div>
                )}
              </div>
            ) : (
              <button className="btn btn-primary btn-lg" onClick={() => navigate('/queue')}>
                Find Match
              </button>
            )}
            <Link to="/leaderboard" className="btn btn-outline btn-lg">Leaderboard</Link>
          </div>
        </div>

        {user && (() => {
          const elo        = user.rating ?? 0;
          const rankInfo   = getRankInfo(elo);
          // Level-to-level progress
          const lvlProgress = rankInfo.xpToNext > 0
            ? Math.min(100, Math.max(0, (rankInfo.xpIntoLevel / rankInfo.xpToNext) * 100))
            : 100;
          const nextLevelInfo = getRankInfo(rankInfo.minXp + rankInfo.xpToNext);

          return (
            <div className="home-rank-section">
              {/* ── Daily wins card (left) ── */}
              <div className="stat-card rank-side-card">
                <div className="stat-value">{dailyWins}</div>
                <div className="stat-label">Daily Wins</div>
              </div>

              {/* ── XP progress bar (center) ── */}
              <div className="rank-progress-card" style={{ position: 'relative' }}>
                <button
                  className="rank-info-btn"
                  onClick={() => setShowRankInfo(true)}
                  aria-label="Rank information"
                  title="View all ranks"
                >i</button>

                {/* Top row: rank icon + name/level on left, XP readout on right */}
                <div className="rp-top-row">
                  <div className="rp-identity">
                    <img src={rankInfo.icon} alt={rankInfo.label} className="rp-rank-icon" />
                    <div className="rp-identity-text">
                      <span className="rp-rank-label" style={{ color: rankInfo.color }}>{rankInfo.label}</span>
                      <span className="rp-level-tag">Level {rankInfo.level}</span>
                    </div>
                  </div>
                  <div className="rp-xp-readout">
                    <span className="rp-xp-current" style={{ color: rankInfo.color }}>{rankInfo.xpIntoLevel.toLocaleString()}</span>
                    <span className="rp-xp-denom"> / {rankInfo.xpToNext.toLocaleString()} XP</span>
                  </div>
                </div>

                {/* Bar + endpoint icons */}
                <div className="rp-bar-row">
                  <img src={rankInfo.icon} alt={rankInfo.label} className="rp-ep-icon" title={rankInfo.label} />
                  <div className="rp-bar-outer">
                    <div className="rp-bar-track">
                      <div className="rp-bar-fill" style={{ width: `${lvlProgress}%`, background: rankInfo.color }} />
                    </div>
                  </div>
                  <img src={nextLevelInfo.icon} alt={nextLevelInfo.label} className="rp-ep-icon" title={nextLevelInfo.label} />
                </div>

                {/* Bottom labels: current level on left, next level on right */}
                <div className="rp-bar-labels">
                  <span style={{ color: rankInfo.color }}>Lvl {rankInfo.level}</span>
                  <span style={{ color: nextLevelInfo.color }}>Lvl {nextLevelInfo.level}</span>
                </div>
              </div>

              {/* ── Login streak card (right) ── */}
              <div className="stat-card rank-side-card">
                <div className="stat-value stat-streak-value">
                  {user.login_streak ?? 0}
                  <img src="/flames-icon.svg" alt="streak" className="stat-streak-icon" />
                </div>
                <div className="stat-label">Day Login Streak</div>
              </div>
            </div>
          );
        })()}

        <div className="home-info">
          <div className="info-card">
            <h3>How It Works</h3>
            <p>Match with a player near your rating. Answer 6 Reading &amp; Writing questions and 6 Math questions in 15 minutes. The highest score wins.</p>
          </div>
          <div className="info-card">
            <h3>XP &amp; Ranks</h3>
            <p>Earn XP every match — for time played, wins, and completing all questions. XP never decreases. Climb from Bronze all the way to Grandmaster.</p>
          </div>
        </div>

        {history.length > 0 && (
          <div style={{ marginTop: 28 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--cb-navy)', marginBottom: 14 }}>Your Recent Matches</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {history.slice(0, 5).map(m => {
                const isP1 = m.player1_id === user?.id;
                const myScore = isP1 ? m.player1_score : m.player2_score;
                const oppScore = isP1 ? m.player2_score : m.player1_score;
                const oppName = isP1 ? m.player2_name : m.player1_name;
                const won = m.winner_id === user?.id;
                const draw = !m.winner_id;
                const delta = isP1 ? m.rating_delta_p1 : m.rating_delta_p2;
                return (
                  <div key={m.id} className="card" style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{ width: 52, textAlign: 'center', fontWeight: 800, fontSize: 14,
                      color: won ? 'var(--cb-green)' : draw ? 'var(--cb-yellow)' : 'var(--cb-red)' }}>
                      {won ? 'WIN' : draw ? 'DRAW' : 'LOSS'}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>vs. {oppName}</div>
                      <div style={{ fontSize: 13, color: 'var(--cb-gray-500)' }}>
                        {myScore} – {oppScore} · {new Date(m.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: delta >= 0 ? 'var(--cb-green)' : 'var(--cb-red)' }}>
                      {delta >= 0 ? '+' : ''}{delta}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── Rank Info Popup ── */}
      {showRankInfo && (
        <RankPathPopup onClose={() => setShowRankInfo(false)} userXp={user?.rating ?? 0} />
      )}

      {/* ── Right sidebar: Recent Global Matches ── */}
      <div className="home-sidebar">
        <div className="rg-panel">
          <div className="rg-panel-title">Recent Global Matches</div>
          {recentGames.length === 0 ? (
            <div className="rg-empty">No matches yet.</div>
          ) : (
            recentGames.map(m => {
              const isDraw = !m.winner_id;
              const p1Won = m.winner_id === m.player1_id;
              const r1 = getRankInfo(m.player1_rating ?? 0);
              const r2 = getRankInfo(m.player2_rating ?? 0);
              return (
                <div key={m.id} className="rg-row">
                  <div className="rg-col">
                    <div className="rg-name-row">
                      <button
                        className={`rg-name rg-name-btn${p1Won && !isDraw ? ' rg-win' : !p1Won && !isDraw ? ' rg-loss' : ''}`}
                        style={{ color: r1.color, display: 'flex', alignItems: 'center', gap: 4, minWidth: 0, overflow: 'hidden' }}
                        onClick={e => openPopup(m.player1_id, e.clientX, e.clientY)}
                      >
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 2, flexShrink: 0,
                          background: `${r1.color}18`, border: `1.2px solid ${r1.color}44`,
                          borderRadius: 4, padding: '0px 4px', fontSize: 9, fontWeight: 800,
                          color: r1.color,
                        }}>
                          {r1.level}
                          <img src={r1.icon} alt={r1.label} title={r1.label} className="rg-rank-icon" style={{ margin: 0 }} />
                        </span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{m.player1_name}</span>
                      </button>
                      {p1Won && !isDraw && <CrownIcon />}
                    </div>
                    {m.player1_school && <span className="rg-school">{m.player1_school}</span>}
                    <span className={`rg-result${isDraw ? ' rg-result-draw' : p1Won ? ' rg-result-win' : ' rg-result-loss'}`}>
                      {isDraw ? 'Draw' : p1Won ? 'Win' : 'Loss'}
                    </span>
                  </div>
                  <div className="rg-middle">
                    <span className="rg-score-num">{m.player1_score}</span>
                    <span className="rg-sep">–</span>
                    <span className="rg-score-num">{m.player2_score}</span>
                  </div>
                  <div className="rg-col rg-col-right">
                    <div className="rg-name-row rg-name-row-right">
                      {!p1Won && !isDraw && <CrownIcon />}
                      <button
                        className={`rg-name rg-name-btn${!p1Won && !isDraw ? ' rg-win' : p1Won && !isDraw ? ' rg-loss' : ''}`}
                        style={{ color: r2.color, display: 'flex', alignItems: 'center', gap: 4, minWidth: 0, overflow: 'hidden' }}
                        onClick={e => openPopup(m.player2_id, e.clientX, e.clientY)}
                      >
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{m.player2_name}</span>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 2, flexShrink: 0,
                          background: `${r2.color}18`, border: `1.2px solid ${r2.color}44`,
                          borderRadius: 4, padding: '0px 4px', fontSize: 9, fontWeight: 800,
                          color: r2.color,
                        }}>
                          {r2.level}
                          <img src={r2.icon} alt={r2.label} title={r2.label} className="rg-rank-icon" style={{ margin: 0 }} />
                        </span>
                      </button>
                    </div>
                    {m.player2_school && <span className="rg-school">{m.player2_school}</span>}
                    <span className={`rg-result${isDraw ? ' rg-result-draw' : !p1Won ? ' rg-result-win' : ' rg-result-loss'}`}>
                      {isDraw ? 'Draw' : !p1Won ? 'Win' : 'Loss'}
                    </span>
                  </div>
                  <span className="rg-ago">{timeAgo(m.created_at)}</span>
                </div>
              );
            })
          )}
        </div>
      </div>

    </div>
  );
}

function RankPathPopup({ onClose, userXp }) {
  const scrollRef = React.useRef(null);
  const [percentile, setPercentile] = React.useState(null);
  const [playerRank, setPlayerRank] = React.useState(null);

  const allRanks = RANK_DISPLAY.flatMap(g =>
    g.tiers.map(t => ({ ...t, color: g.color, group: g.group }))
  );
  const N = allRanks.length;

  const totalXp  = Math.max(0, userXp ?? 0);
  const rankInfo = getRankInfo(totalXp);
  const { currentMin, nextMin } = getTierBounds(totalXp);

  const userRankLabel = rankInfo.label;
  const userIdx = allRanks.findIndex(r =>
    r.label === userRankLabel ||
    (userRankLabel.startsWith('Grandmaster') && r.group === 'Grandmaster')
  );

  const xpForTier      = nextMin - currentMin;
  const withinProgress = xpForTier > 0 ? Math.min(1, (totalXp - currentMin) / xpForTier) : 1;
  const fillFraction   = N > 1 ? Math.min((userIdx + withinProgress) / (N - 1), 1) : 1;

  const CARD_W = 400;

  // Cumulative minXp per rank card (derived from xpToNext chain)
  const rankMinXp = React.useMemo(() => {
    let cum = 0;
    return allRanks.map(rank => {
      const min = cum;
      if (rank.xpToNext) cum += rank.xpToNext;
      return min;
    });
  }, []); // eslint-disable-line

  // How many levels span each rank's segment on the bar
  function levelsInSegment(rank) {
    if (!rank.xpToNext) return 1;
    const xpPerLv = rank.group === 'Bronze' ? 500 : 750;
    return Math.max(1, Math.round(rank.xpToNext / xpPerLv));
  }

  // XP per level for display (e.g. "500 XP" or "750 XP")
  function xpPerLevel(rank) {
    if (!rank.xpToNext) return null;
    return rank.group === 'Bronze' ? 500 : 750;
  }

  // Level-boundary gap positions as fractions [0,1]
  const levelGaps = [];
  allRanks.slice(0, -1).forEach((rank, i) => {
    const lc = levelsInSegment(rank);
    for (let j = 1; j < lc; j++) levelGaps.push((i + j / lc) / (N - 1));
  });

  // Level number labels per segment
  const segmentLevels = (() => {
    let lvl = 1;
    return allRanks.slice(0, -1).map(rank => {
      const lc = levelsInSegment(rank);
      const levels = Array.from({ length: lc }, (_, j) => lvl + j);
      lvl += lc;
      return levels;
    });
  })();

  // XP-cost label per segment (one per rank tier, centered in segment)
  // Show "X XP/lv" for multi-level tiers, "X XP" for single-level
  const segmentXpLabels = allRanks.slice(0, -1).map((rank, i) => {
    const lc = levelsInSegment(rank);
    const xpLv = xpPerLevel(rank);
    if (!xpLv) return null;
    const frac = (i + 0.5) / (N - 1);
    return { frac, text: lc > 1 ? `${xpLv.toLocaleString()} XP/lv` : `${xpLv.toLocaleString()} XP` };
  });

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el || userIdx < 0) return;
    setTimeout(() => {
      const cardCenter = 24 + userIdx * CARD_W + CARD_W / 2;
      el.scrollLeft = cardCenter - el.clientWidth / 2;
    }, 80);
  }, []); // eslint-disable-line

  React.useEffect(() => {
    if (userXp == null) return;
    axios.get(`${API}/api/rank-percentile?rating=${Math.max(0, userXp)}`)
      .then(r => { setPercentile(r.data.percentile); setPlayerRank(r.data.rank); })
      .catch(() => {});
  }, [userXp]); // eslint-disable-line

  // Rank group boundaries for decorative accents above cards
  const groupChanges = new Set(
    allRanks.slice(1).map((r, i) => (r.group !== allRanks[i].group ? i + 1 : -1)).filter(x => x >= 0)
  );

  return (
    <div className="rank-info-overlay" onClick={onClose}>
      <div className="rank-info-popup rank-info-popup--path" onClick={e => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="rip-path-header">
          <button className="rip-close rip-close-abs" onClick={onClose}>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>
          <div className="rip-hrk-row">
            <img src={rankInfo.icon} alt={rankInfo.label} className="rip-hrk-icon" />
            <span className="rip-hrk-label" style={{ color: rankInfo.color, fontFamily: 'Impact, "Arial Narrow", Arial, sans-serif' }}>
              {rankInfo.label.toUpperCase()}
            </span>
          </div>
          <div className="rip-path-title">
            {percentile != null ? (
              <>
                top <span style={{ color: rankInfo.color, fontWeight: 800 }}>{percentile}%</span> of players{playerRank != null ? ` (#${playerRank.toLocaleString()})` : ''}
              </>
            ) : 'Rank Progression'}
          </div>
        </div>

        {/* ── Horizontal scroll ── */}
        <div className="rip-hscroll-wrap" ref={scrollRef}>
          <div className="rip-hscroll-inner" style={{ width: N * CARD_W + 48 }}>

            {/* Group color accents above cards */}
            <div className="rip-hgroup-accents">
              {allRanks.map((rank, i) => (
                <div key={i} className="rip-hgroup-accent-cell" style={{ width: CARD_W }}>
                  <div
                    className="rip-hgroup-accent-bar"
                    style={{
                      background: rank.color,
                      opacity: groupChanges.has(i) ? 1 : 0.18,
                      height: groupChanges.has(i) ? 3 : 2,
                    }}
                  />
                </div>
              ))}
            </div>

            {/* Rank cards */}
            <div className="rip-hcards">
              {allRanks.map((rank, i) => {
                const isCurrent = i === userIdx;
                return (
                  <div key={i} className="rip-hcard" style={{ width: CARD_W }}>
                    <div className="rip-hcard-bg" style={isCurrent ? { borderColor: rank.color } : {}}>
                      <img src={rank.icon} alt={rank.label} className="rip-hcard-icon" />
                    </div>
                    <div
                      className="rip-hcard-name"
                      style={{
                        color: rank.color,
                        fontFamily: 'Impact, "Arial Narrow", Arial, sans-serif',
                        fontSize: rank.label.length > 11 ? '11px' : '13px',
                      }}
                    >
                      {rank.label.toUpperCase()}
                    </div>
                    {/* XP range subtitle */}
                    <div className="rip-hcard-xprange">
                      {rank.levels}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Stems */}
            <div className="rip-hstem-row">
              {allRanks.map((rank, i) => (
                <div key={i} className="rip-hstem-cell" style={{ width: CARD_W }}>
                  <div className="rip-hstem-line" style={{ background: rank.color, opacity: 0.45 }} />
                </div>
              ))}
            </div>

            {/* Track + annotations */}
            <div className="rip-htrack-wrap" style={{ margin: `0 ${CARD_W / 2}px` }}>

              {/* XP cost labels above the track */}
              <div className="rip-hxp-labels">
                {segmentXpLabels.map((seg, i) => seg && (
                  <span key={i} className="rip-hxp-label" style={{ left: `${seg.frac * 100}%` }}>
                    {seg.text}
                  </span>
                ))}
              </div>

              <div className="rip-htrack-container">
                <div className="rip-htrack-bg" />
                <div className="rip-htrack-fill" style={{
                  width: `${fillFraction * 100}%`,
                  background: fillFraction < 0.02
                    ? allRanks[0].color
                    : `linear-gradient(to right, ${allRanks[0].color}, ${rankInfo.color})`,
                }} />
                {levelGaps.map((frac, gi) => (
                  <div key={gi} className="rip-htrack-gap" style={{ left: `${frac * 100}%` }} />
                ))}
                {/* Rank-group boundary ticks */}
                {allRanks.slice(1).map((rank, i) =>
                  groupChanges.has(i + 1) ? (
                    <div
                      key={`gt-${i}`}
                      className="rip-htrack-grouptick"
                      style={{ left: `${((i + 1) / (N - 1)) * 100}%`, background: rank.color }}
                    />
                  ) : null
                )}
                {/* Current-position marker */}
                {userIdx >= 0 && (
                  <div className="rip-htrack-marker" style={{ left: `${fillFraction * 100}%`, background: rankInfo.color }} />
                )}
              </div>

              {/* Level number labels */}
              <div className="rip-hlevel-labels">
                {segmentLevels.map((levels, i) =>
                  levels.map((lv, j) => {
                    const frac = (i + (j + 0.5) / levels.length) / (N - 1);
                    return (
                      <span key={`lbl-${i}-${j}`} className="rip-hlevel-label" style={{ left: `${frac * 100}%` }}>
                        {lv}
                      </span>
                    );
                  })
                )}
              </div>

            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="rip-hfooter">
          <img src={rankInfo.icon} alt={rankInfo.label} className="rip-hfooter-icon" />
          <div className="rip-hfooter-info">
            <span className="rip-hfooter-rank" style={{ color: rankInfo.color, fontFamily: 'Impact, "Arial Narrow", Arial, sans-serif', letterSpacing: '0.04em' }}>
              {rankInfo.label.toUpperCase()}
            </span>
            <span className="rip-hfooter-sub">
              Level {rankInfo.level} · {rankInfo.xpIntoLevel.toLocaleString()} / {rankInfo.xpToNext.toLocaleString()} XP this level
            </span>
          </div>
          <div className="rip-hfooter-total">
            <span className="rip-hfooter-totalxp">{totalXp.toLocaleString()}</span>
            <span className="rip-hfooter-totallabel">total XP</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function CrownIcon() {
  return (
    <svg width="12" height="10" viewBox="0 0 13 11" fill="none" xmlns="http://www.w3.org/2000/svg"
      style={{ flexShrink: 0, display: 'block', alignSelf: 'center' }}>
      <path d="M1 9.5 L2.5 4 L5 6.5 L6.5 1 L8 6.5 L10.5 4 L12 9.5 Z" fill="#c9940a" stroke="#c9940a" strokeWidth="0.5" strokeLinejoin="round"/>
      <rect x="1" y="9.5" width="11" height="1.5" rx="0.75" fill="#c9940a"/>
    </svg>
  );
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}
